export function removeCSSClass(ele, cls) {
	const reg = new RegExp("(\\s|^)" + cls + "(\\s|$)");
	ele.className = ele.className.replace(reg, " ");
}

export function addCSSClass(ele, cls) {
	ele.classList.add(cls);
}

export const toAbsoluteUrl = (pathname) => process.env.PUBLIC_URL + pathname;

export function setupAxios(axios, store) {
	axios.interceptors.request.use(
		(config) => {
			const {
				auth: { authToken },
			} = store.getState();

			if (authToken) {
				config.headers.Authorization = `Bearer ${authToken}`;
			}

			return config;
		},
		(err) => Promise.reject(err)
	);
}

/*  removeStorage: removes a key from localStorage and its sibling expiracy key
		params:
				key <string>     : localStorage key to remove
		returns:
				<boolean> : telling if operation succeeded
 */
export function removeStorage(key) {
	try {
		localStorage.setItem(key, "");
		localStorage.setItem(key + "_expiresIn", "");
		localStorage.removeItem("setting");
	} catch (e) {
		console.log("removeStorage: Error removing key [" + key + "] from localStorage: " + JSON.stringify(e));
		return false;
	}
	return true;
}

/*  getStorage: retrieves a key from localStorage previously set with setStorage().
		params:
				key <string> : localStorage key
		returns:
				<string> : value of localStorage key
				null : in case of expired key or failure
 */
export function getStorage(key) {
	const now = Date.now(); //epoch time, lets deal only with integer
	// set expiration for storage
	let expiresIn = localStorage.getItem(key + "_expiresIn");
	if (expiresIn === undefined || expiresIn === null) {
		expiresIn = 0;
	}

	expiresIn = Math.abs(expiresIn);
	if (expiresIn < now) {
		// Expired
		removeStorage(key);
		return null;
	} else {
		try {
			const value = localStorage.getItem(key);
			return value;
		} catch (e) {
			console.log("getStorage: Error reading key [" + key + "] from localStorage: " + JSON.stringify(e));
			return null;
		}
	}
}
/*  setStorage: writes a key into localStorage setting a expire time
		params:
				key <string>     : localStorage key
				value <string>   : localStorage value
				expires <number> : number of seconds from now to expire the key
		returns:
				<boolean> : telling if operation succeeded
 */
export function setStorage(key, value, expires) {
	if (expires === undefined || expires === null) {
		expires = 24 * 60 * 60; // default: seconds for 1 day
	}

	const now = Date.now(); //millisecs since epoch time, lets deal only with integer
	const schedule = now + expires * 1000;
	try {
		localStorage.setItem(key, value);
		localStorage.setItem(key + "_expiresIn", schedule);
	} catch (e) {
		console.log("setStorage: Error setting key [" + key + "] in localStorage: " + JSON.stringify(e));
		return false;
	}
	return true;
}

export const customRound = (number) => {
	const parts = number.toString().split('.');

	if (parts.length === 2 && parts[1].length > 2) {
		// Nếu có phần thập phân và dài hơn 2 chữ số, làm tròn về 2 chữ số
		return Math.floor(number * 100) / 100;
	}

	// Nếu không thì giữ nguyên
	return number;
}

export const PERMISSIONS_PLAN = {
	"cer_for_system.1": "Certification Programme for 1 Standard System",
	upload_certificate_n_license: "Upload your awarded Certificate and License",
	dashboard_tasking_calendar_audit:
		"Analytical Dashboard for Tasking with calendar view and System readiness for audit",
	"tasking_coordination.5": "Currently the task in our system for FREE PlAN remained unlimited",
	// "tasking_coordination.5": "Set up to 5 tasking for your coordination works between user(s)",
	digital_doc_setup:
		"Set up your cloud-based digital documentation of manual, standard procedures so it always available",
	"audit_record_storage.unlimited":
		"Save your unlimited digital records for the audit preparation so it easy for reference",
	upload_files_photos: "Upload-able on Files & Photos",
	// "team_setup.3": "Currently FREE PLAN will not have Your Team feature, and thus no department feature.",
	// "team_setup.3": "Create your team with our easy multi-user(s) sign up – 3 pax",
	security_logs_capturing: "Security logs capturing all actions with date & time by Whom for accountabilities",
	non_conformity_reporting: "Non conformity reporting feature",
	past_certification_cycle_records:
		"All past records & document are saved under Previous cycle of Certification programme for easy reference",
	"free_storage.500MB": "Free 500 MB storage size",
	storage_add_on: "Storage add on when needed",
	"cer_for_system.10": "Certification Programme for 10 Standard Systems",
	dashboard_tasking_audit_metrics:
		"Analytical Dashboard for Tasking with calendar view, System readiness for audit and performance indicators on total Incidents with category, Total Lost time Injuries (Hours), Total Nonconformity report under category",
	"tasking_coordination.unlimited": "Set up to unlimited tasking for your coordination works between user(s)",
	"digital_doc_setup.unlimited":
		"Set up your cloud-based unlimited digital documentation of manual, standard procedures so it always available",
	"team_setup.unlimited": "Create your team with our easy multi-user(s) sign up – unlimited pax",
	system_n_security_logs_capturing:
		"System & Security logs capturing all actions with date & time by Whom for accountabilities",
	incident_reporting: "Incident reporting feature available",
	public_reporting: "Public reporting with publication feature available for interested parties",
	archived_certification_records:
		"All past records & document are saved under Previous completed Cycle(s) of Certification programme for easy reference",
	"free_storage.5GB": "Free 5 GB storage size",
	add_on_subsidiary_pro: "Add-on subsidiary or multi-site(s) under same account enjoy Pro-Plan same features at $100",
};

export const PERMISSIONS_PRO = [
	"cer_for_system.10",
	"upload_certificate_n_license",
	"dashboard_tasking_audit_metrics",
	"tasking_coordination.unlimited",
	"digital_doc_setup.unlimited",
	"audit_record_storage.unlimited",
	"upload_files_photos",
	"team_setup.unlimited",
	"system_n_security_logs_capturing",
	"non_conformity_reporting",
	"incident_reporting",
	"public_reporting",
	"archived_certification_records",
	"free_storage.5GB",
	"storage_add_on",
	"add_on_subsidiary_pro",
];

export const PERMISSIONS_FREE = [
	"cer_for_system.1",
	"upload_certificate_n_license",
	"dashboard_tasking_calendar_audit",
	"tasking_coordination.5",
	"digital_doc_setup",
	"audit_record_storage.unlimited",
	"upload_files_photos",
	"team_setup.3",
	"security_logs_capturing",
	"non_conformity_reporting",
	"past_certification_cycle_records",
	"free_storage.500MB",
	"storage_add_on",
];

export const OWNER_STATUS = {
	ACCEPTED: { key: "accepted", title: "Accepted" },
	INVITATION_SENT: { key: "invitation_sent", title: "Invitation sent" },
	REJECT: { key: "reject", title: "Rejected" },
};